import { useState } from "react";
import { useQueryClient } from "react-query";

import { AxiosError } from "axios";

import { useProjectContext } from "components/project/projects/context";

import { StickDataQueryKey } from "../../pad-generator/hooks/useStickData";
import { UserSyncMetadata, useFetchSticksMetadata } from "./useFetchSticksMetadata";
import { useSyncSticks } from "./useSyncSticks";

export interface UseSyncSticksControllerProps {
  schema: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export interface UseSyncSticksControllerReturn {
  syncSticks: () => void;
  isSyncing: boolean;
  syncError: string | null;
  syncSuccess: boolean;
  syncMessage: string | null;
  metadata: UserSyncMetadata | undefined;
  isLoadingMetadata: boolean;
  metadataError: Error | null;
}

export const useSyncSticksController = ({
  schema,
  onSuccess,
  onError
}: UseSyncSticksControllerProps): UseSyncSticksControllerReturn => {
  const queryClient = useQueryClient();
  const syncSticksMutation = useSyncSticks();
  const { selectedProjectId: projectId } = useProjectContext();

  // Fetch metadata
  const {
    data: metadata,
    isLoading: isLoadingMetadata,
    error: metadataError
  } = useFetchSticksMetadata(schema);

  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const syncSticks = () => {
    // Reset state before sync
    setSyncError(null);
    setSyncSuccess(false);
    setSyncMessage(null);

    syncSticksMutation.mutate(
      {
        schema,
        projectId
      },
      {
        onSuccess: (response) => {
          if (response.success) {
            setSyncSuccess(true);
            setSyncMessage(response.message);

            // Invalidate stick data queries to refetch updated data
            queryClient.invalidateQueries([StickDataQueryKey]);
            queryClient.invalidateQueries(["scenarioFields"]);
            queryClient.invalidateQueries(["sticksMetadata", schema]);

            // Call optional success callback
            if (onSuccess) {
              onSuccess(response.message);
            }
          } else {
            // Backend returned success: false
            setSyncError(response.message || "Sync failed");
            if (onError) {
              onError(response.message || "Sync failed");
            }
          }
        },
        onError: (error: AxiosError<{ error?: string; message?: string }>) => {
          const errorMessage =
            error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Failed to sync sticks";

          setSyncError(errorMessage);
          setSyncSuccess(false);

          // Call optional error callback
          if (onError) {
            onError(errorMessage);
          }
        }
      }
    );
  };

  return {
    syncSticks,
    isSyncing: syncSticksMutation.isLoading,
    syncError,
    syncSuccess,
    syncMessage,
    metadata,
    isLoadingMetadata,
    metadataError
  };
};
